/**
 * Crops Management Redux Slice
 * 
 * Manages state for crop species and varieties in Redux store.
 * Provides caching and state management for crop reference data.
 */

import { emptyArray } from '@/store/emptyValues';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  CropSpecies,
  CropSpeciesListItem,
  CropSpeciesListResponse,
  CreateCropSpeciesRequest,
  CropVariety,
  CropVarietyListItem,
  CropVarietyListResponse,
  CreateCropVarietyRequest,
} from '@/types/crops';
import cropsApi from '@/services/cropsApi';

// ============================================================================
// Crop Species Slice
// ============================================================================

interface CropSpeciesState {
  species: CropSpeciesListItem[];
  selectedSpecies: CropSpecies | null;
  loading: boolean;
  error: string | null;
  lastFetch: number | null; // Timestamp of last fetch for caching
}

const initialSpeciesState: CropSpeciesState = {
  species: [],
  selectedSpecies: null,
  loading: false,
  error: null,
  lastFetch: null,
};

// Async Thunks
export const fetchCropSpecies = createAsyncThunk(
  'cropSpecies/fetchCropSpecies',
  async (params?: { page?: number; size?: number; preferred_lang?: string }) => {
    const response = await cropsApi.getCropSpecies(params);
    return response;
  }
);

export const fetchCropSpeciesById = createAsyncThunk(
  'cropSpecies/fetchCropSpeciesById',
  async (speciesId: number) => {
    const response = await cropsApi.getCropSpeciesById(speciesId);
    return response;
  }
);

export const createCropSpecies = createAsyncThunk(
  'cropSpecies/createCropSpecies',
  async (data: CreateCropSpeciesRequest) => {
    const response = await cropsApi.createCropSpecies(data);
    return response;
  }
);

export const updateCropSpecies = createAsyncThunk(
  'cropSpecies/updateCropSpecies',
  async ({ id, data }: { id: number; data: Partial<CreateCropSpeciesRequest> }) => {
    const response = await cropsApi.updateCropSpecies(id, data);
    return response;
  }
);

export const deleteCropSpecies = createAsyncThunk(
  'cropSpecies/deleteCropSpecies',
  async (speciesId: number) => {
    await cropsApi.deleteCropSpecies(speciesId);
    return speciesId;
  }
);

// Slice
const cropSpeciesSlice = createSlice({
  name: 'cropSpecies',
  initialState: initialSpeciesState,
  reducers: {
    selectCropSpecies: (state, action: PayloadAction<CropSpecies>) => {
      state.selectedSpecies = action.payload;
    },
    clearSelectedCropSpecies: (state) => {
      state.selectedSpecies = null;
    },
    clearCropSpeciesError: (state) => {
      state.error = null;
    },
    // Manually add a species to the list (e.g., after creating via dialog)
    addCropSpeciesToList: (state, action: PayloadAction<CropSpeciesListItem>) => {
      state.species.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch Crop Species
    builder
      .addCase(fetchCropSpecies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCropSpecies.fulfilled, (state, action) => {
        state.loading = false;
        state.species = action.payload.items;
        state.lastFetch = Date.now();
      })
      .addCase(fetchCropSpecies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch crop species';
      });

    // Fetch Crop Species By ID
    builder
      .addCase(fetchCropSpeciesById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCropSpeciesById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSpecies = action.payload;
      })
      .addCase(fetchCropSpeciesById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch crop species details';
      });

    // Create Crop Species
    builder
      .addCase(createCropSpecies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCropSpecies.fulfilled, (state, action) => {
        state.loading = false;
        // Add to species list - convert to list item format
        const newSpeciesItem: CropSpeciesListItem = {
          id: action.payload.id,
          species_key: action.payload.species_key,
          name: action.payload.translations[0]?.name || action.payload.species_key,
          is_system: action.payload.is_system,
          is_custom: !action.payload.is_system,
        };
        state.species.push(newSpeciesItem);
      })
      .addCase(createCropSpecies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create crop species';
      });

    // Update Crop Species
    builder
      .addCase(updateCropSpecies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCropSpecies.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.species.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.species[index] = {
            ...state.species[index],
            species_key: action.payload.species_key,
            name: action.payload.translations[0]?.name || action.payload.species_key,
          };
        }
        if (state.selectedSpecies?.id === action.payload.id) {
          state.selectedSpecies = action.payload;
        }
      })
      .addCase(updateCropSpecies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update crop species';
      });

    // Delete Crop Species
    builder
      .addCase(deleteCropSpecies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCropSpecies.fulfilled, (state, action) => {
        state.loading = false;
        state.species = state.species.filter((s) => s.id !== action.payload);
        if (state.selectedSpecies?.id === action.payload) {
          state.selectedSpecies = null;
        }
      })
      .addCase(deleteCropSpecies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete crop species';
      });
  },
});

export const {
  selectCropSpecies,
  clearSelectedCropSpecies,
  clearCropSpeciesError,
  addCropSpeciesToList,
} = cropSpeciesSlice.actions;

export const cropSpeciesReducer = cropSpeciesSlice.reducer;

// Selectors
export const selectAllCropSpecies = (state: { cropSpecies: CropSpeciesState }) => 
  state.cropSpecies.species;
export const selectSelectedCropSpecies = (state: { cropSpecies: CropSpeciesState }) => 
  state.cropSpecies.selectedSpecies;
export const selectCropSpeciesLoading = (state: { cropSpecies: CropSpeciesState }) => 
  state.cropSpecies.loading;
export const selectCropSpeciesError = (state: { cropSpecies: CropSpeciesState }) => 
  state.cropSpecies.error;
export const selectCropSpeciesLastFetch = (state: { cropSpecies: CropSpeciesState }) => 
  state.cropSpecies.lastFetch;

// ============================================================================
// Crop Variety Slice
// ============================================================================

interface CropVarietyState {
  varieties: Record<number, CropVarietyListItem[]>; // speciesId -> varieties
  selectedVariety: CropVariety | null;
  loading: boolean;
  error: string | null;
}

const initialVarietyState: CropVarietyState = {
  varieties: {},
  selectedVariety: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchCropVarietiesBySpecies = createAsyncThunk(
  'cropVarieties/fetchCropVarietiesBySpecies',
  async (params: { speciesId: number; page?: number; size?: number; preferred_lang?: string }) => {
    const { speciesId, ...rest } = params;
    const response = await cropsApi.getCropVarietiesBySpecies(speciesId, rest);
    return { speciesId, varieties: response.items };
  }
);

export const fetchCropVarietyById = createAsyncThunk(
  'cropVarieties/fetchCropVarietyById',
  async (varietyId: number) => {
    const response = await cropsApi.getCropVarietyById(varietyId);
    return response;
  }
);

export const createCropVariety = createAsyncThunk(
  'cropVarieties/createCropVariety',
  async (data: CreateCropVarietyRequest) => {
    const response = await cropsApi.createCropVariety(data);
    return response;
  }
);

export const updateCropVariety = createAsyncThunk(
  'cropVarieties/updateCropVariety',
  async ({ id, data }: { id: number; data: Partial<CreateCropVarietyRequest> }) => {
    const response = await cropsApi.updateCropVariety(id, data);
    return response;
  }
);

export const deleteCropVariety = createAsyncThunk(
  'cropVarieties/deleteCropVariety',
  async (varietyId: number) => {
    await cropsApi.deleteCropVariety(varietyId);
    return varietyId;
  }
);

// Slice
const cropVarietySlice = createSlice({
  name: 'cropVarieties',
  initialState: initialVarietyState,
  reducers: {
    selectCropVariety: (state, action: PayloadAction<CropVariety>) => {
      state.selectedVariety = action.payload;
    },
    clearSelectedCropVariety: (state) => {
      state.selectedVariety = null;
    },
    clearCropVarietyError: (state) => {
      state.error = null;
    },
    // Manually add a variety to the list (e.g., after creating via dialog)
    addCropVarietyToList: (state, action: PayloadAction<CropVarietyListItem>) => {
      const speciesId = action.payload.species_id;
      if (!state.varieties[speciesId]) {
        state.varieties[speciesId] = [];
      }
      state.varieties[speciesId].push(action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch Crop Varieties By Species
    builder
      .addCase(fetchCropVarietiesBySpecies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCropVarietiesBySpecies.fulfilled, (state, action) => {
        state.loading = false;
        state.varieties[action.payload.speciesId] = action.payload.varieties;
      })
      .addCase(fetchCropVarietiesBySpecies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch crop varieties';
      });

    // Fetch Crop Variety By ID
    builder
      .addCase(fetchCropVarietyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCropVarietyById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedVariety = action.payload;
      })
      .addCase(fetchCropVarietyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch crop variety details';
      });

    // Create Crop Variety
    builder
      .addCase(createCropVariety.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCropVariety.fulfilled, (state, action) => {
        state.loading = false;
        const speciesId = action.payload.species_id;
        if (!state.varieties[speciesId]) {
          state.varieties[speciesId] = [];
        }
        // Add to varieties list - convert to list item format
        const newVarietyItem: CropVarietyListItem = {
          id: action.payload.id,
          species_id: action.payload.species_id,
          variety_key: action.payload.variety_key,
          name: action.payload.translations[0]?.name || action.payload.variety_key,
          is_system: action.payload.is_system,
          is_custom: !action.payload.is_system,
        };
        state.varieties[speciesId].push(newVarietyItem);
      })
      .addCase(createCropVariety.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create crop variety';
      });

    // Update Crop Variety
    builder
      .addCase(updateCropVariety.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCropVariety.fulfilled, (state, action) => {
        state.loading = false;
        const variety = action.payload;
        const speciesVarieties = state.varieties[variety.species_id];
        if (speciesVarieties) {
          const index = speciesVarieties.findIndex((v) => v.id === variety.id);
          if (index !== -1) {
            speciesVarieties[index] = {
              ...speciesVarieties[index],
              variety_key: variety.variety_key,
              name: variety.translations[0]?.name || variety.variety_key,
            };
          }
        }
        if (state.selectedVariety?.id === variety.id) {
          state.selectedVariety = variety;
        }
      })
      .addCase(updateCropVariety.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update crop variety';
      });

    // Delete Crop Variety
    builder
      .addCase(deleteCropVariety.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCropVariety.fulfilled, (state, action) => {
        state.loading = false;
        Object.keys(state.varieties).forEach((speciesId) => {
          const speciesVarieties = state.varieties[Number(speciesId)];
          if (speciesVarieties) {
            state.varieties[Number(speciesId)] = speciesVarieties.filter(
              (v) => v.id !== action.payload
            );
          }
        });
        if (state.selectedVariety?.id === action.payload) {
          state.selectedVariety = null;
        }
      })
      .addCase(deleteCropVariety.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete crop variety';
      });
  },
});

export const {
  selectCropVariety,
  clearSelectedCropVariety,
  clearCropVarietyError,
  addCropVarietyToList,
} = cropVarietySlice.actions;

export const cropVarietyReducer = cropVarietySlice.reducer;

// Selectors
export const selectCropVarietiesBySpeciesId = (speciesId: number) => (state: { cropVarieties: CropVarietyState }) => 
  state.cropVarieties.varieties[speciesId] || emptyArray<CropVarietyListItem>();
export const selectSelectedCropVariety = (state: { cropVarieties: CropVarietyState }) => 
  state.cropVarieties.selectedVariety;
export const selectCropVarietyLoading = (state: { cropVarieties: CropVarietyState }) => 
  state.cropVarieties.loading;
export const selectCropVarietyError = (state: { cropVarieties: CropVarietyState }) => 
  state.cropVarieties.error;
